import React, { useEffect, useState, memo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ChevronLeft, CheckCircle2, AlertCircle, ZoomIn, ZoomOut, Maximize, Sparkles, Share2, Linkedin, Twitter, Facebook, Mail, Copy, Check } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Separator } from '../components/ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Checkbox } from '../components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { articles, sources, tierLabels, sentimentLabels, antisemitismLabels, contentRoleLabels } from '../lib/data';
import { AntisemitismPanel } from '../components/AntisemitismPanel';
type SocialPlatform = 'linkedin' | 'twitter' | 'facebook' | 'email';
function ShareDialog({
  article,
  open,
  onOpenChange
}: {
  article: (typeof articles)[0];
  open: boolean;
  onOpenChange: (o: boolean) => void;
}) {
  const [platform, setPlatform] = useState<SocialPlatform>('linkedin');
  const [copied, setCopied] = useState(false);
  const summary = `${article.title}\n\n${article.excerpt}\n\nFonte: ${article.source} — ${new Date(article.date).toLocaleDateString('it-IT', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  })}`;
  const hashtags = '#MediaIntelligence #UCEI #ComunitaEbraiche';
  const platformTemplates: Record<SocialPlatform, string> = {
    linkedin: `📰 ${article.title}\n\n${article.excerpt}\n\nLeggi l'articolo completo su ${article.source}.\n\n${hashtags}`,
    twitter: `${article.title} — ${article.source}\n\n"${article.excerpt.slice(0, 140)}..."\n\n${hashtags}`,
    facebook: `${article.title}\n\n${article.excerpt}\n\nFonte: ${article.source}`,
    email: `Ciao,\n\nti segnalo questo articolo pubblicato su ${article.source}:\n\n"${article.title}"\n\n${article.excerpt}\n\nBuona lettura.`
  };
  const [editableText, setEditableText] = useState(platformTemplates.linkedin);
  useEffect(() => {
    setEditableText(platformTemplates[platform]);
    setCopied(false);
  }, [platform, article.id]);
  const platforms: {
    id: SocialPlatform;
    label: string;
    icon: React.ElementType;
    color: string;
    bg: string;
  }[] = [{
    id: 'linkedin',
    label: 'LinkedIn',
    icon: Linkedin,
    color: '#0A66C2',
    bg: '#0A66C2'
  }, {
    id: 'twitter',
    label: 'X / Twitter',
    icon: Twitter,
    color: '#000000',
    bg: '#000000'
  }, {
    id: 'facebook',
    label: 'Facebook',
    icon: Facebook,
    color: '#1877F2',
    bg: '#1877F2'
  }, {
    id: 'email',
    label: 'Email',
    icon: Mail,
    color: '#6B7280',
    bg: '#6B7280'
  }];
  const currentPlatform = platforms.find(p => p.id === platform)!;
  const CurrentIcon = currentPlatform.icon;
  const handleCopy = () => {
    navigator.clipboard.writeText(editableText);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  const charCount = editableText.length;
  const charLimit = platform === 'twitter' ? 280 : platform === 'linkedin' ? 3000 : null;
  return <Dialog open={open} onOpenChange={onOpenChange} data-id="element-467">
      <DialogContent className="sm:max-w-[640px] max-h-[90vh] overflow-y-auto" data-id="element-468">
        <DialogHeader data-id="element-469">
          <DialogTitle className="flex items-center gap-2" data-id="element-470">
            <Share2 className="h-5 w-5 text-accent" data-id="element-471" />
            Condividi articolo
          </DialogTitle>
          <DialogDescription data-id="element-472">
            Scegli la piattaforma e personalizza il testo prima di pubblicare.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 mt-2" data-id="element-473">
          {/* Platform selector */}
          <div data-id="element-474">
            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2 block" data-id="element-475">
              Piattaforma
            </label>
            <div className="grid grid-cols-4 gap-2" data-id="element-476">
              {platforms.map(p => {
              const Icon = p.icon;
              const isActive = p.id === platform;
              return <button key={p.id} onClick={() => setPlatform(p.id)} className={`flex flex-col items-center gap-2 p-3 rounded-lg border transition-all ${isActive ? 'border-accent bg-accent/5 ring-1 ring-accent/30' : 'border-border bg-card hover:border-accent/40'}`} data-id="element-477">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center text-white" style={{
                  backgroundColor: p.bg
                }} data-id="element-478">
                      <Icon className="h-4 w-4" data-id="element-479" />
                    </div>
                    <span className="text-xs font-medium" data-id="element-480">{p.label}</span>
                  </button>;
            })}
            </div>
          </div>

          {/* Preview card */}
          <div data-id="element-481">
            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2 block" data-id="element-482">
              Anteprima
            </label>
            <div className="border border-border rounded-xl overflow-hidden bg-white" data-id="element-483">
              {/* Header del post */}
              <div className="flex items-center gap-3 p-4 border-b border-border" data-id="element-484">
                <div className="w-10 h-10 rounded-full flex items-center justify-center text-white flex-shrink-0" style={{
                backgroundColor: currentPlatform.bg
              }} data-id="element-485">
                  <CurrentIcon className="h-5 w-5" data-id="element-486" />
                </div>
                <div className="min-w-0 flex-1" data-id="element-487">
                  <div className="font-semibold text-sm text-foreground" data-id="element-488">
                    UCEI · Unione delle Comunità Ebraiche Italiane
                  </div>
                  <div className="text-xs text-muted-foreground" data-id="element-489">
                    {platform === 'email' ? 'Bozza email' : 'Post • Adesso'}
                  </div>
                </div>
              </div>

              {/* Corpo */}
              <div className="p-4" data-id="element-490">
                <div className="text-sm text-foreground whitespace-pre-wrap leading-relaxed" data-id="element-491">
                  {editableText}
                </div>
              </div>

              {/* Card articolo */}
              <div className="mx-4 mb-4 border border-border rounded-lg overflow-hidden bg-muted/20" data-id="element-492">
                <div className="px-4 py-3" data-id="element-493">
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1" data-id="element-494">
                    {article.source}
                  </div>
                  <div className="text-sm font-semibold text-foreground line-clamp-2" data-id="element-495">
                    {article.title}
                  </div>
                  <div className="text-xs text-muted-foreground mt-1.5" data-id="element-496">
                    {new Date(article.date).toLocaleDateString('it-IT', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric'
                  })}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Editable text */}
          <div data-id="element-497">
            <div className="flex items-center justify-between mb-2" data-id="element-498">
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider" data-id="element-499">
                Testo
              </label>
              {charLimit && <span className={`text-xs tabular-nums ${charCount > charLimit ? 'text-destructive font-medium' : 'text-muted-foreground'}`} data-id="element-500">
                  {charCount}/{charLimit}
                </span>}
            </div>
            <textarea value={editableText} onChange={e => setEditableText(e.target.value)} rows={6} className="w-full px-3 py-2.5 text-sm border border-input rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-accent resize-y" data-id="element-501" />
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2 pt-2" data-id="element-502">
            <Button variant="outline" onClick={handleCopy} className="rounded-full flex-1" data-id="element-503">
              {copied ? <>
                  <Check className="h-4 w-4 mr-2 text-emerald-600" data-id="element-504" />
                  Copiato
                </> : <>
                  <Copy className="h-4 w-4 mr-2" data-id="element-505" />
                  Copia testo
                </>}
            </Button>
            <Button className="rounded-full flex-1 text-white hover:opacity-90" style={{
            backgroundColor: currentPlatform.bg
          }} data-id="element-506">
              <CurrentIcon className="h-4 w-4 mr-2" data-id="element-507" />
              Pubblica su {currentPlatform.label}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>;
}
export function ArticleDetail() {
  const {
    id
  } = useParams();
  const navigate = useNavigate();
  const [shareOpen, setShareOpen] = useState(false);
  // In a real app, we'd fetch the article by ID. For now, we'll just use the first one if not found.
  const article = articles.find(a => a.id === id) || articles[0];
  const analysis = article.antisemitismAnalysis!;
  const ConfidenceDot = ({
    level
  }: {
    level: 'high' | 'medium' | 'low';
  }) => {
    const colors = {
      high: 'bg-sentiment-positive',
      medium: 'bg-yellow-500',
      low: 'bg-sentiment-negative'
    };
    return <div className={`w-2 h-2 rounded-full ${colors[level]} flex-shrink-0`} title={`Confidence: ${level}`} data-id="element-508" />;
  };
  return <div className="h-full flex flex-col bg-background" data-id="element-509">
      {/* Header */}
      <div className="h-14 border-b border-border bg-card flex items-center justify-between px-2 md:px-4 flex-shrink-0" data-id="element-510">
        <div className="flex items-center gap-2 md:gap-4 min-w-0" data-id="element-511">
          <Button variant="ghost" size="icon-sm" onClick={() => navigate('/dashboard')} className="flex-shrink-0" data-id="element-512">
            <ChevronLeft className="h-4 w-4" data-id="element-513" />
          </Button>
          <div className="flex items-center gap-2 min-w-0" data-id="element-514">
            <Badge variant="outline" className="text-[10px] md:text-xs hidden sm:inline-flex flex-shrink-0" data-id="element-515">
              {article.source}
            </Badge>
            <span className="text-xs md:text-sm font-medium truncate max-w-[150px] sm:max-w-[200px] md:max-w-[300px]" data-id="element-516">
              {article.title}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0" data-id="element-517">
          <Button variant="outline" size="sm" onClick={() => setShareOpen(true)} className="rounded-full h-8 px-3 text-xs md:text-sm border-accent text-accent hover:bg-accent/10 hover:text-accent" data-id="element-518">
            <Share2 className="h-3.5 w-3.5 mr-1.5" data-id="element-519" />
            Share
          </Button>
          <Badge variant="secondary" className="bg-blue-100 text-blue-700 text-[10px] md:text-xs" data-id="element-520">
            Da validare
          </Badge>
        </div>
      </div>

      {/* Score Sub-bar (desktop only) */}
      <div className="hidden md:flex items-center justify-between px-6 py-3 border-b border-border bg-muted/10 flex-shrink-0" data-id="element-521">
        <div className="flex items-center gap-8" data-id="element-522">
          <div className="flex items-center gap-2" data-id="element-523">
            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider font-heading" data-id="element-524">
              Score Rilevanza
            </span>
            <span className="text-2xl font-bold text-accent" style={{
            fontVariantNumeric: 'tabular-nums'
          }} data-id="element-525">
              {article.relevanceScore.toFixed(1)}
            </span>
          </div>
          <div className="flex items-center gap-2" data-id="element-526">
            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider font-heading" data-id="element-527">
              AVE
            </span>
            <span className="text-lg font-semibold" style={{
            fontVariantNumeric: 'tabular-nums'
          }} data-id="element-528">
              €{article.ave.toLocaleString('it-IT')}
            </span>
          </div>
          <div className="flex items-center gap-2" data-id="element-529">
            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider font-heading" data-id="element-530">
              Antisemitismo
            </span>
            <span className={`inline-flex items-center gap-1.5 text-sm font-semibold px-2.5 py-1 rounded-full ${analysis.aggregate.level === 'none' ? 'bg-emerald-50 text-emerald-700' : analysis.aggregate.level === 'low' ? 'bg-amber-50 text-amber-700' : analysis.aggregate.level === 'medium' ? 'bg-orange-50 text-orange-700' : 'bg-red-50 text-red-700'}`} data-id="element-531">
              <span className={`w-1.5 h-1.5 rounded-full ${analysis.aggregate.level === 'none' ? 'bg-emerald-500' : analysis.aggregate.level === 'low' ? 'bg-amber-500' : analysis.aggregate.level === 'medium' ? 'bg-orange-500' : 'bg-red-500'}`} data-id="element-532" />
              {antisemitismLabels[analysis.aggregate.level]}
              {analysis.aggregate.level !== 'none' && <span style={{
              fontVariantNumeric: 'tabular-nums'
            }} data-id="element-533">
                  · {analysis.aggregate.score.toFixed(1)}
                </span>}
            </span>
            <span className="text-[10px] text-muted-foreground hidden lg:inline" data-id="element-534">
              {contentRoleLabels[analysis.aggregate.contentRole]}
            </span>
            {analysis.aggregate.hasDisagreement && <span className="text-[10px] text-amber-600 font-medium hidden lg:inline" data-id="element-535">
                · discordanza
              </span>}
          </div>
        </div>
        <Button className="h-10 px-6 text-sm font-medium rounded-full" onClick={() => navigate('/dashboard')} data-id="element-536">
          Approva e Prosegui
        </Button>
      </div>

      {/* Desktop Split View */}
      <div className="hidden md:flex flex-1 overflow-hidden" data-id="element-537">
        {/* Left: PDF Preview (60%) */}
        <div className="w-[60%] border-r border-border bg-muted/30 flex flex-col" data-id="element-538">
          <div className="h-10 border-b border-border flex items-center justify-between px-4 bg-card" data-id="element-539">
            <span className="text-xs font-medium text-muted-foreground" data-id="element-540">
              Pagina {article.page} di 24
            </span>
            <div className="flex items-center gap-1" data-id="element-541">
              <Button variant="ghost" size="icon-sm" data-id="element-542">
                <ZoomOut className="h-4 w-4" data-id="element-543" />
              </Button>
              <span className="text-xs font-medium w-12 text-center" data-id="element-544">100%</span>
              <Button variant="ghost" size="icon-sm" data-id="element-545">
                <ZoomIn className="h-4 w-4" data-id="element-546" />
              </Button>
              <Separator orientation="vertical" className="h-4 mx-1" data-id="element-547" />
              <Button variant="ghost" size="icon-sm" data-id="element-548">
                <Maximize className="h-4 w-4" data-id="element-549" />
              </Button>
            </div>
          </div>

          <div className="flex-1 overflow-auto p-8 flex flex-col items-stretch" data-id="element-550">
            {/* Article title aligned outside the page */}
            <div className="w-full mb-4" data-id="element-551">
              <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-heading mb-1.5" data-id="element-552">
                {article.source}
              </div>
              <h2 className="text-lg md:text-xl font-normal font-heading leading-snug text-foreground" data-id="element-553">
                {article.title}
              </h2>
            </div>
            {/* Simulated PDF Page */}
            <div className="w-full bg-white shadow-sm border border-border relative" data-id="element-554">
              {/* Newspaper image */}
              <img src={article.clipping || 'https://cdn.magicpatterns.com/uploads/bxZusPzb8iMgxXqPBxjz3V/IMG_0001.png'} alt={`${article.source} — ${article.title}`} className="w-full h-auto object-contain" data-id="element-555" />
            </div>
          </div>
        </div>

        {/* Right: Validation Form (40%) */}
        <div className="w-[40%] flex flex-col bg-card" data-id="element-556">
          <div className="flex-1 overflow-auto p-6 space-y-8" data-id="element-557">
            {/* AI Notice */}

            {/* Form Fields */}
            <div className="space-y-6" data-id="element-558">
              {/* Source & Date */}
              <div className="grid grid-cols-2 gap-4" data-id="element-559">
                <div className="space-y-2" data-id="element-560">
                  <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-2" data-id="element-561">
                    Testata <ConfidenceDot level="high" data-id="element-562" />
                  </label>
                  <Select defaultValue={article.source} data-id="element-563">
                    <SelectTrigger data-id="element-564">
                      <SelectValue data-id="element-565" />
                    </SelectTrigger>
                    <SelectContent data-id="element-566">
                      {sources.map(s => <SelectItem key={s.name} value={s.name} data-id="element-567">
                          {s.name}
                        </SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2" data-id="element-568">
                  <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-2" data-id="element-569">
                    Data <ConfidenceDot level="high" data-id="element-570" />
                  </label>
                  <input type="date" className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50" defaultValue={article.date} data-id="element-571" />
                </div>
              </div>

              {/* Title */}
              <div className="space-y-2" data-id="element-572">
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-2" data-id="element-573">
                  Titolo Articolo <ConfidenceDot level="medium" data-id="element-574" />
                </label>
                <textarea className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50" defaultValue={article.title} data-id="element-575" />
              </div>

              {/* Author */}
              <div className="space-y-2" data-id="element-576">
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-2" data-id="element-577">
                  Firma / Autore <ConfidenceDot level="high" data-id="element-578" />
                </label>
                <div className="flex items-center gap-4" data-id="element-579">
                  <input type="text" className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50" defaultValue={article.author} data-id="element-580" />
                  <div className="flex items-center gap-2 flex-shrink-0" data-id="element-581">
                    <Switch id="authoritative" defaultChecked={article.isAuthoritative} data-id="element-582" />
                    <label htmlFor="authoritative" className="text-sm font-medium" data-id="element-583">
                      Autorevole
                    </label>
                  </div>
                </div>
              </div>

              <Separator data-id="element-584" />

              {/* Content Type */}
              <div className="space-y-3" data-id="element-585">
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-2" data-id="element-586">
                  Tipologia Contenuto <ConfidenceDot level="medium" data-id="element-587" />
                </label>
                <RadioGroup defaultValue={article.contentType} className="grid grid-cols-2 gap-2" data-id="element-588">
                  <div className="flex items-center space-x-2" data-id="element-589">
                    <RadioGroupItem value="dedicated_article" id="r1" data-id="element-590" />
                    <label htmlFor="r1" className="text-sm" data-id="element-591">
                      Articolo dedicato
                    </label>
                  </div>
                  <div className="flex items-center space-x-2" data-id="element-592">
                    <RadioGroupItem value="interview" id="r2" data-id="element-593" />
                    <label htmlFor="r2" className="text-sm" data-id="element-594">
                      Intervista
                    </label>
                  </div>
                  <div className="flex items-center space-x-2" data-id="element-595">
                    <RadioGroupItem value="signed_commentary" id="r3" data-id="element-596" />
                    <label htmlFor="r3" className="text-sm" data-id="element-597">
                      Commento firmato
                    </label>
                  </div>
                  <div className="flex items-center space-x-2" data-id="element-598">
                    <RadioGroupItem value="mention" id="r4" data-id="element-599" />
                    <label htmlFor="r4" className="text-sm" data-id="element-600">
                      Citazione
                    </label>
                  </div>
                </RadioGroup>
              </div>

              <Separator data-id="element-601" />

              {/* Positioning */}
              <div className="space-y-3" data-id="element-602">
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-2" data-id="element-603">
                  Posizionamento <ConfidenceDot level="low" data-id="element-604" />
                </label>
                <div className="grid grid-cols-2 gap-3" data-id="element-605">
                  <div className="flex items-center space-x-2" data-id="element-606">
                    <Checkbox id="c1" defaultChecked={article.hasPhoto} data-id="element-607" />
                    <label htmlFor="c1" className="text-sm" data-id="element-608">
                      Foto presente
                    </label>
                  </div>
                  <div className="flex items-center space-x-2" data-id="element-609">
                    <Checkbox id="c2" defaultChecked={article.titleMentionsClient} data-id="element-610" />
                    <label htmlFor="c2" className="text-sm" data-id="element-611">
                      Cliente nel titolo
                    </label>
                  </div>
                  <div className="flex items-center space-x-2" data-id="element-612">
                    <Checkbox id="c3" defaultChecked={article.boldedReference} data-id="element-613" />
                    <label htmlFor="c3" className="text-sm" data-id="element-614">
                      Citazione in grassetto
                    </label>
                  </div>
                  <div className="flex items-center space-x-2" data-id="element-615">
                    <Checkbox id="c4" defaultChecked={article.frontPageCallout} data-id="element-616" />
                    <label htmlFor="c4" className="text-sm" data-id="element-617">
                      Richiamo in prima
                    </label>
                  </div>
                </div>
              </div>

              <Separator data-id="element-618" />

              {/* Sentiment */}
              <div className="space-y-3" data-id="element-619">
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-2" data-id="element-620">
                  Sentiment <ConfidenceDot level="high" data-id="element-621" />
                </label>
                <div className="flex gap-2" data-id="element-622">
                  <Button variant={article.sentiment === 'positive' ? 'default' : 'outline'} className={article.sentiment === 'positive' ? 'bg-sentiment-positive hover:bg-sentiment-positive/90' : ''} data-id="element-623">
                    Positivo
                  </Button>
                  <Button variant={article.sentiment === 'neutral' ? 'default' : 'outline'} className={article.sentiment === 'neutral' ? 'bg-sentiment-neutral hover:bg-sentiment-neutral/90' : ''} data-id="element-624">
                    Neutro
                  </Button>
                  <Button variant={article.sentiment === 'negative' ? 'default' : 'outline'} className={article.sentiment === 'negative' ? 'bg-sentiment-negative hover:bg-sentiment-negative/90' : ''} data-id="element-625">
                    Negativo
                  </Button>
                </div>
              </div>

              <Separator data-id="element-626" />

              {/* Antisemitism Analysis */}
              <AntisemitismPanel analysis={analysis} data-id="element-627" />

              <Separator data-id="element-628" />

              {/* Score Breakdown */}
              <div className="space-y-3" data-id="element-629">
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider font-heading" data-id="element-630">
                  Dettaglio Algoritmo
                </label>
                <div className="bg-muted/30 border border-border rounded-md p-3" data-id="element-631">
                  <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs" data-id="element-632">
                    <div className="flex justify-between" data-id="element-633">
                      <span className="text-muted-foreground" data-id="element-634">
                        Tier testata:
                      </span>
                      <span className="font-medium" data-id="element-635">
                        +{article.scoreBreakdown.tier}
                      </span>
                    </div>
                    <div className="flex justify-between" data-id="element-636">
                      <span className="text-muted-foreground" data-id="element-637">
                        Posizionamento:
                      </span>
                      <span className="font-medium" data-id="element-638">
                        +{article.scoreBreakdown.positioning}
                      </span>
                    </div>
                    <div className="flex justify-between" data-id="element-639">
                      <span className="text-muted-foreground" data-id="element-640">Tipologia:</span>
                      <span className="font-medium" data-id="element-641">
                        +{article.scoreBreakdown.contentType}
                      </span>
                    </div>
                    <div className="flex justify-between" data-id="element-642">
                      <span className="text-muted-foreground" data-id="element-643">Sentiment:</span>
                      <span className="font-medium" data-id="element-644">
                        +{article.scoreBreakdown.sentiment}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Tabbed View */}
      <div className="md:hidden flex-1 flex flex-col overflow-hidden" data-id="element-645">
        <Tabs defaultValue="validation" className="flex-1 flex flex-col h-full" data-id="element-646">
          <div className="px-4 pt-4 pb-2 bg-card border-b border-border flex-shrink-0" data-id="element-647">
            <TabsList className="w-full grid grid-cols-2" data-id="element-648">
              <TabsTrigger value="pdf" className="text-xs" data-id="element-649">
                Anteprima PDF
              </TabsTrigger>
              <TabsTrigger value="validation" className="text-xs" data-id="element-650">
                Validazione
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="pdf" className="flex-1 m-0 overflow-auto bg-muted/30 p-4 flex justify-center" data-id="element-651">
            {/* Mobile: article title aligned outside the page */}
            <div className="w-full flex flex-col" data-id="element-652">
              <div className="mb-3" data-id="element-653">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-heading mb-1" data-id="element-654">
                  {article.source}
                </div>
                <h2 className="text-base font-normal font-heading leading-snug text-foreground" data-id="element-655">
                  {article.title}
                </h2>
              </div>
              {/* Mobile Simulated PDF Page */}
              <div className="w-full bg-white shadow-sm border border-border relative origin-top" data-id="element-656">
                <img src={article.clipping || 'https://cdn.magicpatterns.com/uploads/bxZusPzb8iMgxXqPBxjz3V/IMG_0001.png'} alt={`${article.source} — ${article.title}`} className="w-full h-auto object-contain" data-id="element-657" />
              </div>
            </div>
          </TabsContent>

          <TabsContent value="validation" className="flex-1 m-0 overflow-auto flex flex-col bg-card" data-id="element-658">
            <div className="flex-1 p-4 space-y-6" data-id="element-659">
              {/* AI Notice */}
              <div className="bg-blue-50 border border-blue-100 rounded-md p-3 flex items-start gap-2" data-id="element-660">
                <Sparkles className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" data-id="element-661" />
                <div data-id="element-662">
                  <h4 className="text-xs font-medium text-blue-900" data-id="element-663">
                    Estrazione AI completata
                  </h4>
                  <p className="text-[10px] text-blue-700 mt-1" data-id="element-664">
                    I campi con il pallino verde hanno un'alta confidenza.
                  </p>
                </div>
              </div>

              {/* Form Fields - Mobile Layout */}
              <div className="space-y-5" data-id="element-665">
                <div className="grid grid-cols-2 gap-3" data-id="element-666">
                  <div className="space-y-1.5" data-id="element-667">
                    <label className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-1.5" data-id="element-668">
                      Testata <ConfidenceDot level="high" data-id="element-669" />
                    </label>
                    <Select defaultValue={article.source} data-id="element-670">
                      <SelectTrigger className="h-9 text-xs" data-id="element-671">
                        <SelectValue data-id="element-672" />
                      </SelectTrigger>
                      <SelectContent data-id="element-673">
                        {sources.map(s => <SelectItem key={s.name} value={s.name} className="text-xs" data-id="element-674">
                            {s.name}
                          </SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5" data-id="element-675">
                    <label className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-1.5" data-id="element-676">
                      Data <ConfidenceDot level="high" data-id="element-677" />
                    </label>
                    <input type="date" className="flex h-9 w-full rounded-md border border-input bg-background px-2 py-1 text-xs ring-offset-background" defaultValue={article.date} data-id="element-678" />
                  </div>
                </div>

                <div className="space-y-1.5" data-id="element-679">
                  <label className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-1.5" data-id="element-680">
                    Titolo Articolo <ConfidenceDot level="medium" data-id="element-681" />
                  </label>
                  <textarea className="flex min-h-[60px] w-full rounded-md border border-input bg-background px-2 py-2 text-xs ring-offset-background" defaultValue={article.title} data-id="element-682" />
                </div>

                <div className="space-y-1.5" data-id="element-683">
                  <label className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-1.5" data-id="element-684">
                    Firma / Autore <ConfidenceDot level="high" data-id="element-685" />
                  </label>
                  <div className="flex flex-col gap-2" data-id="element-686">
                    <input type="text" className="flex h-9 w-full rounded-md border border-input bg-background px-2 py-1 text-xs ring-offset-background" defaultValue={article.author} data-id="element-687" />
                    <div className="flex items-center gap-2" data-id="element-688">
                      <Switch id="authoritative-mobile" defaultChecked={article.isAuthoritative} className="scale-75 origin-left" data-id="element-689" />
                      <label htmlFor="authoritative-mobile" className="text-xs font-medium" data-id="element-690">
                        Autorevole
                      </label>
                    </div>
                  </div>
                </div>

                <Separator data-id="element-691" />

                <div className="space-y-2" data-id="element-692">
                  <label className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-1.5" data-id="element-693">
                    Tipologia Contenuto <ConfidenceDot level="medium" data-id="element-694" />
                  </label>
                  <RadioGroup defaultValue={article.contentType} className="grid grid-cols-1 gap-2" data-id="element-695">
                    <div className="flex items-center space-x-2" data-id="element-696">
                      <RadioGroupItem value="dedicated_article" id="rm1" className="w-3 h-3" data-id="element-697" />
                      <label htmlFor="rm1" className="text-xs" data-id="element-698">
                        Articolo dedicato
                      </label>
                    </div>
                    <div className="flex items-center space-x-2" data-id="element-699">
                      <RadioGroupItem value="interview" id="rm2" className="w-3 h-3" data-id="element-700" />
                      <label htmlFor="rm2" className="text-xs" data-id="element-701">
                        Intervista
                      </label>
                    </div>
                    <div className="flex items-center space-x-2" data-id="element-702">
                      <RadioGroupItem value="signed_commentary" id="rm3" className="w-3 h-3" data-id="element-703" />
                      <label htmlFor="rm3" className="text-xs" data-id="element-704">
                        Commento firmato
                      </label>
                    </div>
                  </RadioGroup>
                </div>

                <Separator data-id="element-705" />

                <div className="space-y-2" data-id="element-706">
                  <label className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-1.5" data-id="element-707">
                    Posizionamento <ConfidenceDot level="low" data-id="element-708" />
                  </label>
                  <div className="grid grid-cols-1 gap-2" data-id="element-709">
                    <div className="flex items-center space-x-2" data-id="element-710">
                      <Checkbox id="cm1" defaultChecked={article.hasPhoto} className="w-3 h-3" data-id="element-711" />
                      <label htmlFor="cm1" className="text-xs" data-id="element-712">
                        Foto presente
                      </label>
                    </div>
                    <div className="flex items-center space-x-2" data-id="element-713">
                      <Checkbox id="cm2" defaultChecked={article.titleMentionsClient} className="w-3 h-3" data-id="element-714" />
                      <label htmlFor="cm2" className="text-xs" data-id="element-715">
                        Cliente nel titolo
                      </label>
                    </div>
                    <div className="flex items-center space-x-2" data-id="element-716">
                      <Checkbox id="cm3" defaultChecked={article.boldedReference} className="w-3 h-3" data-id="element-717" />
                      <label htmlFor="cm3" className="text-xs" data-id="element-718">
                        Citazione in grassetto
                      </label>
                    </div>
                  </div>
                </div>

                <Separator data-id="element-719" />

                <div className="space-y-2" data-id="element-720">
                  <label className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-1.5" data-id="element-721">
                    Sentiment <ConfidenceDot level="high" data-id="element-722" />
                  </label>
                  <div className="flex gap-1.5" data-id="element-723">
                    <Button variant={article.sentiment === 'positive' ? 'default' : 'outline'} size="sm" className={`h-8 text-[10px] px-2 flex-1 ${article.sentiment === 'positive' ? 'bg-sentiment-positive hover:bg-sentiment-positive/90' : ''}`} data-id="element-724">
                      Positivo
                    </Button>
                    <Button variant={article.sentiment === 'neutral' ? 'default' : 'outline'} size="sm" className={`h-8 text-[10px] px-2 flex-1 ${article.sentiment === 'neutral' ? 'bg-sentiment-neutral hover:bg-sentiment-neutral/90' : ''}`} data-id="element-725">
                      Neutro
                    </Button>
                    <Button variant={article.sentiment === 'negative' ? 'default' : 'outline'} size="sm" className={`h-8 text-[10px] px-2 flex-1 ${article.sentiment === 'negative' ? 'bg-sentiment-negative hover:bg-sentiment-negative/90' : ''}`} data-id="element-726">
                      Negativo
                    </Button>
                  </div>
                </div>

                <Separator data-id="element-727" />

                {/* Antisemitism Analysis (mobile) */}
                <AntisemitismPanel analysis={analysis} data-id="element-728" />
              </div>
            </div>

            {/* Mobile Bottom Score Card */}
            <div className="p-4 border-t border-border bg-muted/10 flex-shrink-0" data-id="element-729">
              <div className="flex items-end justify-between mb-3" data-id="element-730">
                <div data-id="element-731">
                  <div className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider mb-0.5" data-id="element-732">
                    Score Rilevanza
                  </div>
                  <div className="text-3xl font-bold text-accent" style={{
                  fontVariantNumeric: 'tabular-nums'
                }} data-id="element-733">
                    {article.relevanceScore.toFixed(1)}
                  </div>
                </div>
                <div className="text-right" data-id="element-734">
                  <div className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider mb-0.5" data-id="element-735">
                    Valore AVE
                  </div>
                  <div className="text-lg font-semibold" style={{
                  fontVariantNumeric: 'tabular-nums'
                }} data-id="element-736">
                    €{article.ave.toLocaleString('it-IT')}
                  </div>
                </div>
              </div>
              <div className="flex items-center justify-between mb-3" data-id="element-737">
                <span className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider" data-id="element-738">
                  Antisemitismo
                </span>
                <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2 py-0.5 rounded-full ${analysis.aggregate.level === 'none' ? 'bg-emerald-50 text-emerald-700' : analysis.aggregate.level === 'low' ? 'bg-amber-50 text-amber-700' : analysis.aggregate.level === 'medium' ? 'bg-orange-50 text-orange-700' : 'bg-red-50 text-red-700'}`} data-id="element-739">
                  {antisemitismLabels[analysis.aggregate.level]}
                  {analysis.aggregate.level !== 'none' && <span style={{
                  fontVariantNumeric: 'tabular-nums'
                }} data-id="element-740">
                      · {analysis.aggregate.score.toFixed(1)}
                    </span>}
                </span>
              </div>
              <Button className="w-full h-12 text-sm font-medium rounded-full" onClick={() => navigate('/dashboard')} data-id="element-741">
                Approva e Prosegui
              </Button>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      <ShareDialog article={article} open={shareOpen} onOpenChange={setShareOpen} data-id="element-742" />
    </div>;
}